import type { FiltersDefinition, FiltersState } from '@factorialco/f0-react'

import type { FilterKeys } from 'modules/shift_management/hooks/reducers/filter'
import type { CustomFilter } from 'modules/shift_management/hooks/reducers/filter'
import {
  EMPLOYEE_STATUS,
  SHIFT_CONFLICTS,
  SHIFT_WITHOUT_CONFLICTS,
} from 'modules/shift_management/hooks/reducers/filter'
import type ShiftsConflicts from 'modules/shift_management/store/internal/collections/ShiftsConflicts'

const getArray = <T>(v: unknown): T[] => (Array.isArray(v) ? v : [])

type FilterStateNormalized = {
  locationIds: number[]
  employeeIds: string[] | null
  teamIds: number[] | null
  legalEntityIds: number[] | null
  terminated: boolean
  shiftIds: number[] | null
  withoutShiftIds: number[]
  customFilters: CustomFilter[]
}

/**
 * Transforms FilterPicker output (FiltersState) to the internal FilterState format
 *
 * @param filterPickerState - The state from OneFilterPicker component
 * @param filterDefinitions - The filter definitions used in the picker
 * @returns Partial FilterState with the transformed values
 */
const transformFilterStateFormat = (
  filterPickerState: FiltersState<FiltersDefinition<string>>,
  filterKeys: Record<FilterKeys, string>,
  shiftsConflicts: ShiftsConflicts
): FilterStateNormalized => {
  const result: FilterStateNormalized = {
    locationIds: [],
    employeeIds: null,
    teamIds: null,
    terminated: false,
    shiftIds: null,
    legalEntityIds: null,
    withoutShiftIds: [],
    customFilters: [],
  }
  const employees = getArray<string>(filterPickerState[filterKeys.employees])
  const teams = getArray<number>(filterPickerState[filterKeys.teams])
  const statuses = getArray<string>(filterPickerState[filterKeys.status])
  const employeeStatusesValues = getArray<string>(filterPickerState[filterKeys.employee_status])
  const workAreas = getArray<number>(filterPickerState[filterKeys.shift_work_areas])
  const legalEntities = getArray<number>(filterPickerState[filterKeys.legal_entities])
  const shiftLocations = getArray<number>(filterPickerState[filterKeys.shift_locations])

  if (employees.length) {
    result.employeeIds = employees.map(String)
  }

  if (teams.length) {
    result.teamIds = teams.map(Number)
  }

  if (legalEntities.length) {
    result.legalEntityIds = legalEntities.map(Number)
  }

  if (statuses.includes(SHIFT_CONFLICTS)) {
    result.shiftIds = shiftsConflicts.shiftIds
  }

  if (statuses.includes(SHIFT_WITHOUT_CONFLICTS)) {
    result.withoutShiftIds = shiftsConflicts?.shiftIds
  }

  if (workAreas.length) {
    result.customFilters = workAreas.map((workArea) => ({
      field: 'shift_work_area',
      value: workArea.toString(),
      condition: 'is_equal',
    }))
  }

  if (shiftLocations.length) {
    result.customFilters = [
      ...result.customFilters,
      ...shiftLocations.map((locationId) => ({
        field: 'shift_location',
        value: locationId.toString(),
        condition: 'is_equal',
      })),
    ]
  }

  result.terminated = employeeStatusesValues.includes(EMPLOYEE_STATUS.terminated)

  return result
}
export { transformFilterStateFormat }
